/**
 * Type analysis utilities using ts-morph for semantic type checking
 * Replaces string-based type checks with semantic TypeScript type analysis
 */

import type { Type as TSMorphType } from "ts-morph";

export interface TypeAnalyzer {
  isPromise(type: TSMorphType): boolean;
  isRecord(type: TSMorphType): boolean;
  isArray(type: TSMorphType): boolean;
  isArrayBuffer(type: TSMorphType): boolean;
  isDate(type: TSMorphType): boolean;
  isMap(type: TSMorphType): boolean;
  isError(type: TSMorphType): boolean;
  isCustomType(type: TSMorphType): boolean;
  isSyncFunction(type: TSMorphType): boolean;
  isAsyncFunction(type: TSMorphType): boolean;
  isVoid(type: TSMorphType): boolean;
  isBoolean(type: TSMorphType): boolean;
  isString(type: TSMorphType): boolean;
  isNumber(type: TSMorphType): boolean;
  isPrimitive(type: TSMorphType): boolean;
  isObject(type: TSMorphType): boolean;
  isUnion(type: TSMorphType): boolean;
  isOptional(type: TSMorphType): boolean;
  getPromiseResolvedType(type: TSMorphType): TSMorphType | undefined;
  getArrayElementType(type: TSMorphType): TSMorphType | undefined;
  getTypeText(type: TSMorphType): string;
  getFunctionSignature(type: TSMorphType): {
    parameters: Array<{ name: string; type: string; isRest: boolean }>;
    returnType: string;
  } | undefined;
}

/**
 * Creates a type analyzer for semantic TypeScript type checking
 */
export function createTypeAnalyzer(): TypeAnalyzer {
  /**
   * Helper to check if type matches a specific symbol name
   * Handles type aliases by following the alias chain
   */
  function isSymbol(type: TSMorphType, symbolName: string): boolean {
    const symbol = type.getSymbol();
    if (symbol?.getName() === symbolName) {
      return true;
    }

    // Follow type alias chain
    let aliasSymbol = type.getAliasSymbol();
    while (aliasSymbol != null) {
      if (aliasSymbol.getName() === symbolName) {
        return true;
      }
      // Try to get the aliased symbol to continue the chain
      const nextAlias = aliasSymbol.getAliasedSymbol();
      if (nextAlias === aliasSymbol) {
        // Prevent infinite loop if symbol points to itself
        break;
      }
      aliasSymbol = nextAlias;
    }

    return false;
  }

  /**
   * Check if type is a Promise
   */
  function isPromise(type: TSMorphType): boolean {
    return isSymbol(type, "Promise");
  }

  /**
   * Check if type is a Record
   */
  function isRecord(type: TSMorphType): boolean {
    return isSymbol(type, "Record");
  }

  /**
   * Check if type is an array
   */
  function isArray(type: TSMorphType): boolean {
    return type.isArray();
  }

  /**
   * Check if type is ArrayBuffer
   */
  function isArrayBuffer(type: TSMorphType): boolean {
    return isSymbol(type, "ArrayBuffer");
  }

  /**
   * Check if type is Date
   */
  function isDate(type: TSMorphType): boolean {
    return isSymbol(type, "Date");
  }

  /**
   * Check if type is Map
   */
  function isMap(type: TSMorphType): boolean {
    return isSymbol(type, "Map");
  }

  /**
   * Check if type is Error
   */
  function isError(type: TSMorphType): boolean {
    return isSymbol(type, "Error");
  }

  /**
   * Check if type is a custom user-defined type (interface/class)
   */
  function isCustomType(type: TSMorphType): boolean {
    const symbol = type.getSymbol();
    return symbol != null && !isArray(type) && type.isObject();
  }

  /**
   * Check if type is a synchronous function
   */
  function isSyncFunction(type: TSMorphType): boolean {
    const signatures = type.getCallSignatures();
    if (signatures.length === 0) {
      return false;
    }
    // It's a sync function if it has call signatures but doesn't return Promise
    return signatures.some((sig) => {
      const returnType = sig.getReturnType();
      return !isPromise(returnType);
    });
  }

  /**
   * Check if type is an asynchronous function (returns Promise)
   */
  function isAsyncFunction(type: TSMorphType): boolean {
    const signatures = type.getCallSignatures();
    return signatures.some((sig) => {
      const returnType = sig.getReturnType();
      return isPromise(returnType);
    });
  }

  /**
   * Check if type is void
   */
  function isVoid(type: TSMorphType): boolean {
    return type.getText() === "void" || type.isUndefined() || type.isNull();
  }

  /**
   * Check if type is boolean
   */
  function isBoolean(type: TSMorphType): boolean {
    return type.isBoolean() || type.isBooleanLiteral();
  }

  /**
   * Check if type is string
   */
  function isString(type: TSMorphType): boolean {
    return type.isString() || type.isStringLiteral();
  }

  /**
   * Check if type is number
   */
  function isNumber(type: TSMorphType): boolean {
    return type.isNumber() || type.isNumberLiteral();
  }

  /**
   * Check if type is a primitive (string, number, boolean, void)
   */
  function isPrimitive(type: TSMorphType): boolean {
    return (
      isString(type) || isNumber(type) || isBoolean(type) || isVoid(type)
    );
  }

  /**
   * Check if type is an object (not primitive)
   */
  function isObject(type: TSMorphType): boolean {
    return type.isObject() && !isArray(type);
  }

  /**
   * Check if type is a union type
   */
  function isUnion(type: TSMorphType): boolean {
    return type.isUnion();
  }

  /**
   * Check if type is optional (includes undefined)
   */
  function isOptional(type: TSMorphType): boolean {
    if (isUnion(type)) {
      const unionTypes = type.getUnionTypes();
      return unionTypes.some((t) => t.isUndefined() || t.isNull());
    }
    return false;
  }

  /**
   * Get the resolved type from a Promise
   * @returns The type wrapped by Promise, or undefined if not a Promise
   */
  function getPromiseResolvedType(type: TSMorphType): TSMorphType | undefined {
    if (!isPromise(type)) {
      return undefined;
    }
    const typeArgs = type.getTypeArguments();
    return typeArgs[0];
  }

  /**
   * Get the element type from an array
   * @returns The element type, or undefined if not an array
   */
  function getArrayElementType(type: TSMorphType): TSMorphType | undefined {
    if (!isArray(type)) {
      return undefined;
    }
    const arrayType = type.getArrayElementType();
    return arrayType;
  }

  /**
   * Get the text representation of a type
   */
  function getTypeText(type: TSMorphType): string {
    return type.getText();
  }

  /**
   * Get function signature information
   * @returns Parameter and return type information, or undefined if not a function
   */
  function getFunctionSignature(type: TSMorphType): {
    parameters: Array<{ name: string; type: string; isRest: boolean }>;
    returnType: string;
  } | undefined {
    const signatures = type.getCallSignatures();
    if (signatures.length === 0) {
      return undefined;
    }

    const signature = signatures[0];
    if (!signature) {
      return undefined;
    }

    const declaration = signature.getDeclaration();
    const parameters = signature.getParameters().map((param) => {
      const paramType = declaration
        ? param.getTypeAtLocation(declaration as any)
        : param.getDeclaredType();
      return {
        name: param.getName(),
        type: paramType.getText(),
        // Note: isRestParameter is not available on ts-morph Symbol, using a workaround
        isRest: param.getName().startsWith("...") || false,
      };
    });

    const returnType = signature.getReturnType();

    return {
      parameters,
      returnType: returnType.getText(),
    };
  }

  return {
    isPromise,
    isRecord,
    isArray,
    isArrayBuffer,
    isDate,
    isMap,
    isError,
    isCustomType,
    isSyncFunction,
    isAsyncFunction,
    isVoid,
    isBoolean,
    isString,
    isNumber,
    isPrimitive,
    isObject,
    isUnion,
    isOptional,
    getPromiseResolvedType,
    getArrayElementType,
    getTypeText,
    getFunctionSignature,
  };
}
